import DOMPurify, { type Config } from 'dompurify'
import { useEffect, useMemo, useRef } from 'react'
import { createRoot, type Root } from 'react-dom/client'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { MermaidRenderer } from '@/catalog-apps/components/component-library/MdTextComponent/MermaidRenderer'
import { cn } from '@/lib/utils'
import { attachDocsCodeCopyButtons } from './docsCodeCopy'
import { DOCS_COPY_SUCCESS_MESSAGE } from './docsConstants'
import { handleDocsContentClick } from './docsLinkHandler'
import { splitDocsHtml } from './splitDocsHtml'

const SANITIZE_OPTIONS: Config = {
  ADD_TAGS: ['input'],
  ADD_ATTR: ['checked', 'name', 'id', 'type', 'data-tabs', 'open', 'class'],
}

function hydrateNestedMermaid(container: HTMLElement): Root[] {
  const roots: Root[] = []

  container.querySelectorAll('pre.mermaid').forEach((pre) => {
    const chart = (pre.querySelector('code')?.textContent ?? pre.textContent ?? '').trim()
    if (!chart) {
      return
    }

    const mountPoint = document.createElement('div')
    mountPoint.className = 'docs-mermaid my-6'
    pre.replaceWith(mountPoint)

    const root = createRoot(mountPoint)
    root.render(<MermaidRenderer chart={chart} className="docs-mermaid my-6" />)
    roots.push(root)
  })

  return roots
}

type DocsContentProps = {
  html: string
  className?: string
}

export function DocsContent({ html, className = '' }: DocsContentProps) {
  const navigate = useNavigate()
  const contentRef = useRef<HTMLDivElement>(null)
  const blocks = useMemo(() => {
    const sanitized = String(DOMPurify.sanitize(html, SANITIZE_OPTIONS))
    return splitDocsHtml(sanitized)
  }, [html])

  useEffect(() => {
    const root = contentRef.current
    if (!root) {
      return
    }

    attachDocsCodeCopyButtons(root, () => {
      toast.success(DOCS_COPY_SUCCESS_MESSAGE)
    })

    const mermaidRoots = hydrateNestedMermaid(root)

    return () => {
      mermaidRoots.forEach((mermaidRoot) => mermaidRoot.unmount())
    }
  }, [html])

  return (
    <div
      ref={contentRef}
      className={cn('docs-content prose prose-neutral max-w-none pt-14 dark:prose-invert', className)}
      onClick={(event) => handleDocsContentClick(event, navigate)}
    >
      {blocks.map((block, index) =>
        block.type === 'mermaid' ? (
          <MermaidRenderer key={`mermaid-${index}`} chart={block.chart} className="docs-mermaid my-6" />
        ) : (
          <div key={`html-${index}`} dangerouslySetInnerHTML={{ __html: block.content }} />
        ),
      )}
    </div>
  )
}
